import { FC, useEffect, useMemo } from 'react';
import { useStandingMeshStore } from '../Standing/standingMeshStore';
import { Vector3 } from 'three';
import { MetricKeyEnum, useLayerStore } from '../LayerControls/layerStore';
import { useHighlighter } from '../ui/useHighlighter';

interface ThreeDPlaneProps {
  layerKey:
    | MetricKeyEnum.TrunkShift
    | MetricKeyEnum.PlumbShift
    | MetricKeyEnum.MidSacralPlane
    | MetricKeyEnum.MidSagittalPlane
    | MetricKeyEnum.SagittalBalancePlane;
  color: string;
  hipCentroid: number;
  value: number;
}

export const ThreeDPlane: FC<ThreeDPlaneProps> = ({
  hipCentroid,
  value,
  color,
  layerKey,
}) => {
  const { standingMeshBoundingBox } = useStandingMeshStore();
  const { addLayerState, isLayerVisible } = useLayerStore();
  useHighlighter({ layerKey });

  const result = useMemo(() => {
    try {
      if (!standingMeshBoundingBox) {
        // don't throw an error because it might not be loaded yet
        return null;
      }
      const box = standingMeshBoundingBox;
      const center = box.getCenter(new Vector3());
      const size = box.getSize(new Vector3());

      return {
        centerX: center.x,
        centerY: center.y,
        centerZ: center.z,
        sizeX: size.x,
        sizeY: size.y,
        sizeZ: size.z,
      };
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [standingMeshBoundingBox]);

  useEffect(() => {
    if (!result) {
      return;
    }
    addLayerState(layerKey, {
      key: layerKey,
      visible: false,
    });
  }, [addLayerState, layerKey, result]);

  if (!result) {
    return null;
  }

  const showLayer = isLayerVisible(layerKey);

  return (
    <group name={layerKey} visible={showLayer}>
      <mesh position={[value, result.centerY, hipCentroid]} visible={showLayer}>
        <boxGeometry args={[0.005, result.sizeY * 2, result.sizeZ * 2]} />
        <meshBasicMaterial color={color} transparent opacity={0.75} />
      </mesh>
    </group>
  );
};
